import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { invoicesAPI, clientsAPI, itemsAPI, settingsAPI } from '../utils/api';
import { format, addDays } from 'date-fns';
import { 
  FiPlus, FiTrash2, FiSave, FiX, FiSearch
} from 'react-icons/fi';
import './CreateInvoice.css';

const CreateInvoice = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [showItemSearch, setShowItemSearch] = useState(false);
  const [showCustomItemModal, setShowCustomItemModal] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [taxEnabled, setTaxEnabled] = useState(true);
  const [displayFields, setDisplayFields] = useState({
    sku: true,
    category: true,
    item_name: true,
    description: true,
    unit_price: true,
    unit: true,
    quantity: true,
    tax: true,
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  
  const [customItem, setCustomItem] = useState({
    name: '',
    sku: '',
    category: '',
    description: '',
    quantity: 1,
    unitPrice: '',
    unit: 'pcs',
    taxRate: 0,
  });
  
  const [formData, setFormData] = useState({
    clientId: searchParams.get('clientId') || '',
    client: null,
    issueDate: format(new Date(), 'yyyy-MM-dd'),
    dueDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    currency: 'AED',
    poNumber: '',
    notes: '',
    terms: 'Payment is due within 30 days of invoice date.',
    privateNotes: '',
    discountType: 'percentage',
    discountValue: 0,
    items: [],
    bankAccountId: null,
    showBankDetails: false,
  });

  useEffect(() => {
    if (formData.clientId) {
      fetchClient(formData.clientId);
    }
    fetchItems();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.getAll();
      const settings = response.data.settings;
      if (settings.tax_enabled) {
        setTaxEnabled(settings.tax_enabled.value === 'true');
      }
      // Load display fields settings
      if (settings.display_fields) {
        try {
          const parsed = typeof settings.display_fields.value === 'string' 
            ? JSON.parse(settings.display_fields.value) 
            : settings.display_fields.value;
          setDisplayFields(prev => ({ ...prev, ...parsed }));
        } catch (e) {
          console.error('Failed to parse display_fields:', e);
        }
      }
    } catch (error) {
      console.error('Failed to load settings');
    }
    
    // Fetch bank accounts
    try {
      const response = await settingsAPI.getBankAccounts();
      const accounts = response.data.bankAccounts || [];
      setBankAccounts(accounts);
      // Set default bank account if any
      const defaultAccount = accounts.find(acc => acc.is_default);
      if (defaultAccount) {
        setFormData(prev => ({
          ...prev,
          bankAccountId: defaultAccount.id,
          showBankDetails: true
        }));
      }
    } catch (error) {
      console.error('Failed to load bank accounts');
    }
  };

  const fetchClient = async (id) => {
    try {
      const response = await clientsAPI.getById(id);
      setFormData(prev => ({
        ...prev,
        client: response.data.client,
        clientId: id,
        currency: response.data.client.currency || 'AED',
      }));
    } catch (error) {
      toast.error('Failed to load client');
    }
  };

  const fetchItems = async () => {
    try {
      const response = await itemsAPI.getAll({ limit: 100 });
      setItems(response.data.items);
    } catch (error) {
      console.error('Failed to load items');
    }
  };

  const searchClients = async () => {
    try {
      const response = await clientsAPI.getAll({ search: clientSearch, limit: 10 });
      setClients(response.data.clients);
    } catch (error) {
      console.error('Failed to search clients');
    }
  };

  const openClientSearch = async () => {
    setShowClientSearch(true);
    try {
      const response = await clientsAPI.getAll({ limit: 10 });
      setClients(response.data.clients);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const selectClient = (client) => {
    setFormData(prev => ({
      ...prev,
      client,
      clientId: client.id,
      currency: client.currency || 'AED',
    }));
    setShowClientSearch(false);
    setClientSearch('');
  };

  const addItem = (item) => {
    const newItem = {
      itemId: item.id,
      name: item.name,
      sku: item.sku || '',
      category: item.category || '',
      description: item.description || '',
      quantity: 1,
      unitPrice: item.unit_price,
      unit: item.unit || 'pcs',
      taxRate: taxEnabled ? (item.tax_rate || 0) : 0,
      discountType: 'percentage',
      discountValue: 0,
      isCustom: false,
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
    setShowItemSearch(false);
    setItemSearch('');
  };

  const addCustomItem = () => {
    if (!customItem.name || !customItem.unitPrice) {
      toast.error('Item name and unit price are required');
      return;
    }

    const newItem = {
      itemId: null,
      name: customItem.name,
      sku: customItem.sku || '',
      category: customItem.category || '',
      description: customItem.description || '',
      quantity: parseFloat(customItem.quantity) || 1,
      unitPrice: parseFloat(customItem.unitPrice),
      unit: customItem.unit || 'pcs',
      taxRate: taxEnabled ? (parseFloat(customItem.taxRate) || 0) : 0,
      discountType: 'percentage',
      discountValue: 0,
      isCustom: true,
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
    
    setCustomItem({
      name: '',
      sku: '',
      category: '',
      description: '',
      quantity: 1,
      unitPrice: '',
      unit: 'pcs',
      taxRate: 0,
    });
    setShowCustomItemModal(false);
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, items: newItems };
    });
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unitPrice;
    const discount = item.discountType === 'percentage' 
      ? subtotal * (item.discountValue / 100)
      : item.discountValue;
    const afterDiscount = subtotal - discount;
    const tax = afterDiscount * (item.taxRate / 100);
    return {
      subtotal,
      discount,
      tax,
      total: afterDiscount + tax,
    };
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    formData.items.forEach(item => {
      const calc = calculateItemTotal(item);
      subtotal += calc.subtotal;
      totalTax += calc.tax;
      totalDiscount += calc.discount;
    });

    const invoiceDiscount = formData.discountValue > 0
      ? (formData.discountType === 'percentage'
        ? (subtotal - totalDiscount) * (formData.discountValue / 100)
        : formData.discountValue)
      : 0;

    const total = subtotal - totalDiscount - invoiceDiscount + totalTax;

    return {
      subtotal,
      tax: totalTax,
      discount: totalDiscount + invoiceDiscount,
      total,
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: formData.currency,
    }).format(amount || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.clientId) {
      toast.error('Please select a client');
      return;
    }

    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setLoading(true);

    try {
      const data = {
        clientId: parseInt(formData.clientId),
        issueDate: formData.issueDate,
        dueDate: formData.dueDate,
        currency: formData.currency,
        poNumber: formData.poNumber,
        notes: formData.notes,
        terms: formData.terms,
        privateNotes: formData.privateNotes,
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue) || 0,
        bankAccountId: formData.bankAccountId,
        showBankDetails: formData.showBankDetails,
        items: formData.items.map(item => ({
          itemId: item.itemId,
          name: item.name,
          sku: item.sku || '',
          category: item.category || '',
          description: item.description,
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          unit: item.unit,
          taxRate: parseFloat(item.taxRate) || 0,
          discountType: item.discountType,
          discountValue: parseFloat(item.discountValue) || 0,
        })),
      };

      const response = await invoicesAPI.create(data);
      toast.success('Invoice created successfully');
      navigate(`/invoices/${response.data.invoice.id}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <div className="page-content create-invoice-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Create Invoice</h1>
          <p>Create a new invoice for your client</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/invoices')}>
            <FiX /> Cancel
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="invoice-form-grid">
          <div className="invoice-details">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Client</h3>
              </div>
              <div className="card-body">
                {formData.client ? (
                  <div className="selected-client">
                    <div className="client-info">
                      <h4>{formData.client.company_name}</h4>
                      <p>{formData.client.contact_person}</p>
                      <p className="text-muted">{formData.client.email}</p>
                    </div>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={openClientSearch}>
                      Change
                    </button>
                  </div>
                ) : (
                  <button type="button" className="btn btn-secondary btn-block" onClick={openClientSearch}>
                    <FiSearch /> Select Client
                  </button>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Invoice Details</h3>
              </div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Issue Date</label>
                    <input
                      type="date"
                      value={formData.issueDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, issueDate: e.target.value }))}
                      className="form-control"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label required">Due Date</label>
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                      className="form-control"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                      className="form-control"
                    >
                      <option value="AED">AED - UAE Dirham</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">PO Number</label>
                    <input
                      type="text"
                      value={formData.poNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, poNumber: e.target.value }))}
                      className="form-control"
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="form-control"
                    rows={3}
                    placeholder="Notes visible to client"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Terms & Conditions</label>
                  <textarea
                    value={formData.terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                    className="form-control"
                    rows={3}
                  />
                </div>

                {bankAccounts.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Bank Account for Payment</label>
                    <select
                      value={formData.bankAccountId || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        bankAccountId: e.target.value ? parseInt(e.target.value) : null,
                        showBankDetails: e.target.value ? true : false
                      }))}
                      className="form-control"
                    >
                      <option value="">Don't show bank details</option>
                      {bankAccounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.bank_name} - {account.account_name}
                        </option>
                      ))}
                    </select>
                    <span className="form-hint">Select a bank account to display its details on the invoice PDF</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="invoice-items">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Items</h3>
                <div className="card-header-actions">
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowItemSearch(true)}>
                    <FiPlus /> From Catalog
                  </button>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowCustomItemModal(true)}>
                    <FiPlus /> Custom
                  </button>
                </div>
              </div>
              <div className="card-body">
                {formData.items.length === 0 ? (
                  <div className="empty-state">
                    <p>No items added yet</p>
                    <div className="empty-state-actions">
                      <button type="button" className="btn btn-primary" onClick={() => setShowItemSearch(true)}>
                        <FiPlus /> From Catalog
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => setShowCustomItemModal(true)}>
                        <FiPlus /> Custom Item
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="items-list">
                    {formData.items.map((item, index) => {
                      const calc = calculateItemTotal(item);
                      return (
                        <div key={index} className={`invoice-item ${item.isCustom ? 'custom-item' : ''}`}>
                          <div className="item-header">
                            <div className="item-name-input">
                              <span className="item-index">{index + 1}</span>
                              <input
                                type="text"
                                value={item.name}
                                onChange={(e) => updateItem(index, 'name', e.target.value)}
                                className="form-control item-name-field"
                                placeholder="Item name"
                              />
                              {item.isCustom && <span className="custom-badge">Custom</span>}
                            </div>
                            <button type="button" className="btn btn-sm btn-danger item-remove-btn" onClick={() => removeItem(index)}>
                              <FiTrash2 />
                            </button>
                          </div>
                          <div className="item-fields" style={{ padding: '16px', display: 'block' }}>
                            {(displayFields.sku || displayFields.category) && (
                              <div className="form-row" style={{ marginBottom: '12px' }}>
                                {displayFields.sku && (
                                  <div className="form-group">
                                    <label className="form-label">SKU</label>
                                    <input
                                      type="text"
                                      value={item.sku || ''}
                                      onChange={(e) => updateItem(index, 'sku', e.target.value)}
                                      className="form-control"
                                      placeholder="SKU"
                                    />
                                  </div>
                                )}
                                {displayFields.category && (
                                  <div className="form-group">
                                    <label className="form-label">Category</label>
                                    <input
                                      type="text"
                                      value={item.category || ''}
                                      onChange={(e) => updateItem(index, 'category', e.target.value)}
                                      className="form-control"
                                      placeholder="Category"
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                            {displayFields.description && (
                            <div className="form-group description-group" style={{ marginBottom: '12px' }}>
                              <label className="form-label">Description</label>
                              <textarea
                                value={item.description}
                                onChange={(e) => updateItem(index, 'description', e.target.value)}
                                className="form-control"
                                rows={2}
                                placeholder="Item description..."
                              />
                            </div>
                            )}
                            <div className="item-pricing-row" style={{ display: 'grid', gridTemplateColumns: `72px 1fr ${taxEnabled ? '72px' : ''} 150px`, gap: '12px', alignItems: 'end' }}>
                              {(
                                <div className="form-group">
                                  <label className="form-label">Qty</label>
                                  <input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                    className="form-control"
                                    min="0.01"
                                    step="0.01"
                                  />
                                </div>
                              )}
                              {(
                                <div className="form-group">
                                  <label className="form-label">Unit Price</label>
                                  <input
                                    type="number"
                                    value={item.unitPrice}
                                    onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                                    className="form-control"
                                    step="0.01"
                                    min="0"
                                  />
                                </div>
                              )}
                              {taxEnabled && (
                                <div className="form-group">
                                  <label className="form-label">Tax %</label>
                                  <input
                                    type="number"
                                    value={item.taxRate}
                                    onChange={(e) => updateItem(index, 'taxRate', e.target.value)}
                                    className="form-control"
                                    step="0.01"
                                    min="0"
                                  />
                                </div>
                              )}
                              <div className="item-total-col">
                                <label className="form-label">Amount</label>
                                {/* Pre-VAT line amount, matching the final document */}
                                <div className="item-total-value">{formatCurrency(calc.total - calc.tax)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {formData.items.length > 0 && (
                  <div className="invoice-totals">
                    <div className="total-row">
                      <span>Subtotal</span>
                      <span>{formatCurrency(totals.subtotal)}</span>
                    </div>
                    <div className="total-row discount-input-row">
                      <span>Discount</span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="number"
                          value={formData.discountValue}
                          onChange={(e) => setFormData(prev => ({ ...prev, discountValue: e.target.value }))}
                          className="form-control"
                          step="0.01"
                          min="0"
                          style={{ width: '100px' }}
                          placeholder="0"
                        />
                        <select
                          value={formData.discountType}
                          onChange={(e) => setFormData(prev => ({ ...prev, discountType: e.target.value }))}
                          className="form-control"
                          style={{ width: '85px' }}
                        >
                          <option value="percentage">%</option>
                          <option value="fixed">AED</option>
                        </select>
                      </div>
                    </div>
                    {totals.discount > 0 && (
                      <div className="total-row discount">
                        <span>Discount Amount</span>
                        <span>-{formatCurrency(totals.discount)}</span>
                      </div>
                    )}
                    {taxEnabled && (
                      <div className="total-row">
                        <span>Tax</span>
                        <span>{formatCurrency(totals.tax)}</span>
                      </div>
                    )}
                    <div className="total-row grand-total">
                      <span>Total</span>
                      <span>{formatCurrency(totals.total)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg btn-block"
                  disabled={loading || formData.items.length === 0}
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Creating...
                    </>
                  ) : (
                    <>
                      <FiSave /> Create Invoice
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Client Search Modal */}
      {showClientSearch && (
        <div className="modal-overlay" onClick={() => setShowClientSearch(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Select Client</h2>
              <button className="modal-close" onClick={() => setShowClientSearch(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="search-bar">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search clients..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                />
                <button type="button" className="btn btn-primary" onClick={searchClients}>
                  Search
                </button>
              </div>
              <div className="client-list">
                {clients.map(client => (
                  <div key={client.id} className="client-option" onClick={() => selectClient(client)}>
                    <div className="client-option-name">{client.company_name}</div>
                    <div className="client-option-email">{client.email}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item Search Modal */}
      {showItemSearch && (
        <div className="modal-overlay" onClick={() => setShowItemSearch(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Select Item from Catalog</h2>
              <button className="modal-close" onClick={() => setShowItemSearch(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="search-bar">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search items..."
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                />
              </div>
              <div className="item-list">
                {items
                  .filter(item => 
                    item.is_active && 
                    (item.name.toLowerCase().includes(itemSearch.toLowerCase()) ||
                     (item.sku && item.sku.toLowerCase().includes(itemSearch.toLowerCase()))))
                  .map(item => (
                    <div key={item.id} className="item-option" onClick={() => addItem(item)}>
                      <div className="item-option-name">{item.name}</div>
                      <div className="item-option-price">{formatCurrency(item.unit_price)} / {item.unit}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Item Modal */}
      {showCustomItemModal && (
        <div className="modal-overlay" onClick={() => setShowCustomItemModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add Custom Item</h2>
              <button className="modal-close" onClick={() => setShowCustomItemModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label required">Item Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={customItem.name}
                  onChange={(e) => setCustomItem(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter item name"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">SKU</label>
                  <input
                    type="text"
                    className="form-control"
                    value={customItem.sku}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, sku: e.target.value }))}
                    placeholder="Enter SKU (optional)"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <input
                    type="text"
                    className="form-control"
                    value={customItem.category}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="Enter category (optional)"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  value={customItem.description}
                  onChange={(e) => setCustomItem(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter description (optional)"
                  rows={2}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">Quantity</label>
                  <input
                    type="number"
                    className="form-control"
                    value={customItem.quantity}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, quantity: e.target.value }))}
                    min="0.01"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Unit</label>
                  <select
                    className="form-control"
                    value={customItem.unit}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, unit: e.target.value }))}
                  >
                    <option value="pcs">Pieces (pcs)</option>
                    <option value="hrs">Hours (hrs)</option>
                    <option value="days">Days</option>
                    <option value="units">Units</option>
                    <option value="m">Meters (m)</option>
                    <option value="kg">Kilograms (kg)</option>
                    <option value="l">Liters (l)</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">Unit Price</label>
                  <input
                    type="number"
                    className="form-control"
                    value={customItem.unitPrice}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, unitPrice: e.target.value }))}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
                {taxEnabled && (
                  <div className="form-group">
                    <label className="form-label">Tax Rate (%)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={customItem.taxRate}
                      onChange={(e) => setCustomItem(prev => ({ ...prev, taxRate: e.target.value }))}
                      step="0.01"
                      min="0"
                      placeholder="0"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowCustomItemModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={addCustomItem}>
                <FiPlus /> Add Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateInvoice;